/**
 * Diagnostic Logger for Production Rendering Issues
 * 
 * Temporary diagnostic logging to identify production rendering problems.
 * This should be removed after the issue is resolved.
 * 
 * NOTE: Uses window.console directly to bypass safe-console-replacement script.
 * These console statements are intentional for diagnostics and will be removed
 * after the rendering issue is resolved.
 * 
 * @author AI Assistant
 * @version 1.0.0
 */

import { logger } from './logger';

/** Dev-only: avoid production console noise and extra work (also reduces bot/crawler oddities). */
const DIAGNOSTIC_ENABLED = import.meta.env.DEV;

// Use window.console to bypass the safe-console-replacement script
// This allows console logging in production for diagnostics
const diagnosticConsole = typeof window !== 'undefined' ? window.console : console;

/**
 * Log component render start
 */
export function logComponentStart(componentName: string, props?: any): void {
  if (!DIAGNOSTIC_ENABLED) return;
  
  const message = `[DIAGNOSTIC] ${componentName} component - STARTING RENDER`;
  logger.debug(message, props || {});
  diagnosticConsole.log(message, props || {});
}

/**
 * Log component render complete
 */
export function logComponentComplete(componentName: string): void {
  if (!DIAGNOSTIC_ENABLED) return;
  
  const message = `[DIAGNOSTIC] ${componentName} component - RENDER COMPLETE`;
  logger.debug(message);
  diagnosticConsole.log(message);
}

/**
 * Log component render error
 */
export function logComponentError(componentName: string, error: Error): void {
  if (!DIAGNOSTIC_ENABLED) return;
  
  const message = `[DIAGNOSTIC] ${componentName} component - RENDER ERROR`;
  logger.error(message, error);
  diagnosticConsole.error(message, error);
}

/**
 * Log chunk loading attempt
 */
export function logChunkLoadAttempt(chunkName: string): void {
  if (!DIAGNOSTIC_ENABLED) return;
  
  const message = `[DIAGNOSTIC] Attempting to load chunk: ${chunkName}`;
  logger.debug(message);
  diagnosticConsole.log(message);
}

/**
 * Log chunk loading success
 */
export function logChunkLoadSuccess(chunkName: string): void {
  if (!DIAGNOSTIC_ENABLED) return;
  
  const message = `[DIAGNOSTIC] Successfully loaded chunk: ${chunkName}`;
  logger.debug(message);
  diagnosticConsole.log(message);
}

/**
 * Log chunk loading failure
 */
export function logChunkLoadFailure(chunkName: string, error: Error): void {
  if (!DIAGNOSTIC_ENABLED) return;
  
  const message = `[DIAGNOSTIC] Failed to load chunk: ${chunkName}`;
  logger.error(message, error);
  diagnosticConsole.error(message, error);
}

/**
 * Log Suspense boundary state
 */
export function logSuspenseState(componentName: string, state: 'loading' | 'resolved' | 'rejected'): void {
  if (!DIAGNOSTIC_ENABLED) return;
  
  const message = `[DIAGNOSTIC] Suspense boundary for ${componentName}: ${state}`;
  logger.debug(message);
  diagnosticConsole.log(message);
}

/**
 * Log API call
 */
export function logApiCall(endpoint: string, status?: number): void {
  if (!DIAGNOSTIC_ENABLED) return;
  
  const message = `[DIAGNOSTIC] API call: ${endpoint}${status ? ` (${status})` : ''}`;
  logger.debug(message);
  diagnosticConsole.log(message);
}

/**
 * Log React render phase
 */
export function logReactPhase(phase: string, details?: any): void {
  if (!DIAGNOSTIC_ENABLED) return;
  
  const message = `[DIAGNOSTIC] React phase: ${phase}`;
  logger.debug(message, details || {});
  diagnosticConsole.log(message, details || {});
}
